import { EvaluationAnalytics, AIExecutionScore, EvaluationExecution } from '../types/evaluation';

/**
 * Distribution of scores bucketed into integer ranges.
 * For overall (1-10): keys 1..10 → count.
 * For breakdown (0-1): keys 0..10 representing 0-10%, 10-20%, ..., 90-100%.
 */
export type ScoreDistribution = Record<number, number>;

/**
 * Per-provider AI score statistics including breakdown averages and distributions.
 */
export interface AIScoreStatsEntry {
  average: number;
  count: number;
  missing: number;
  breakdownAverages: {
    accuracy: number;
    citationQuality: number;
    completeness: number;
    legalQuality: number;
  };
  overallDistribution: ScoreDistribution;
  breakdownDistributions: {
    accuracy: ScoreDistribution;
    citationQuality: ScoreDistribution;
    completeness: ScoreDistribution;
    legalQuality: ScoreDistribution;
  };
}

export type AIScoreStatsMap = Record<string, AIScoreStatsEntry>;

/**
 * Calculate per-provider AI score statistics from executions and batch AI scores.
 * Extracted from EvaluationAnalyticsView.loadAIScoreStats for reuse.
 */
export function calculateAIScoreStats(
  analyticsData: EvaluationAnalytics,
  executions: EvaluationExecution[],
  aiScores: Record<string, AIExecutionScore[]>
): { stats: AIScoreStatsMap; totalMissing: number } {
  const stats: AIScoreStatsMap = {};
  let totalMissing = 0;

  analyticsData.providerMetrics.forEach(provider => {
    const providerScores: number[] = [];
    const accuracyScores: number[] = [];
    const citationScores: number[] = [];
    const completenessScores: number[] = [];
    const legalScores: number[] = [];
    let missingCount = 0;

    executions.forEach(exec => {
      const providerResult = exec.providerResults.find(pr => pr.providerId === provider.providerId);
      if (providerResult) {
        const executionScores = aiScores[exec.id] || [];
        const providerScore = executionScores.find(s => s.providerId === provider.providerId);

        if (providerScore) {
          providerScores.push(providerScore.overallScore);
          accuracyScores.push(providerScore.breakdown.accuracy);
          citationScores.push(providerScore.breakdown.citationQuality);
          completenessScores.push(providerScore.breakdown.completeness);
          legalScores.push(providerScore.breakdown.legalQuality);
        } else {
          if (providerResult.response && !providerResult.response.startsWith('Error:')) {
            missingCount++;
            totalMissing++;
          }
        }
      }
    });

    const avg = (arr: number[]) => arr.length > 0 ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;

    // Build overall distribution (1-10 integer buckets)
    const overallDist: ScoreDistribution = {};
    for (let i = 1; i <= 10; i++) overallDist[i] = 0;
    providerScores.forEach(s => {
      const bucket = Math.min(10, Math.max(1, Math.round(s)));
      overallDist[bucket] = (overallDist[bucket] || 0) + 1;
    });

    // Build breakdown distributions (0-10 buckets representing 0-10%..90-100%)
    const buildBreakdownDist = (scores: number[]): ScoreDistribution => {
      const dist: ScoreDistribution = {};
      for (let i = 0; i <= 10; i++) dist[i] = 0;
      scores.forEach(s => {
        const bucket = Math.min(10, Math.max(0, Math.floor(s * 10)));
        dist[bucket] = (dist[bucket] || 0) + 1;
      });
      return dist;
    };

    stats[provider.providerId] = {
      average: avg(providerScores),
      count: providerScores.length,
      missing: missingCount,
      breakdownAverages: {
        accuracy: avg(accuracyScores),
        citationQuality: avg(citationScores),
        completeness: avg(completenessScores),
        legalQuality: avg(legalScores),
      },
      overallDistribution: overallDist,
      breakdownDistributions: {
        accuracy: buildBreakdownDist(accuracyScores),
        citationQuality: buildBreakdownDist(citationScores),
        completeness: buildBreakdownDist(completenessScores),
        legalQuality: buildBreakdownDist(legalScores),
      },
    };
  });

  return { stats, totalMissing };
}
